import { useEffect, useRef, useState } from "preact/hooks";
import type { App } from "../app.ts";
import { isKeptImage } from "../lib/images.ts";
import type { Preview as Post } from "../lib/preview.ts";
import { useAppState } from "./hooks.ts";

type SiteTheme = "light" | "dark";

/** The open file as the site would show it, in the site's own light or dark. */
export function Preview({ app }: { app: App }) {
  const s = useAppState(app);
  const [theme, setTheme] = useState<SiteTheme>(() => app.effectiveTheme());
  const root = useRef<HTMLDivElement>(null);

  // The Markdown renderer and the sanitizer load the first time a preview opens.
  const [post, setPost] = useState<Post | null>(null);
  useEffect(() => {
    if (!s.previewOpen) return void setPost(null);
    let stale = false;
    const text = app.currentText();
    Promise.all([import("../lib/preview.ts"), import("dompurify")]).then(([lib, purify]) => {
      if (stale) return;
      const built = lib.buildPreview(text);
      setPost({ ...built, html: purify.default.sanitize(built.html) });
    });
    return () => {
      stale = true;
    };
  }, [s.previewOpen, s.current?.path, s.fmRev, app]);

  // Focus moves into the preview once it has content, so Escape closes it.
  const ready = post !== null;
  useEffect(() => {
    if (s.previewOpen && ready) root.current?.focus();
  }, [s.previewOpen, ready]);

  // A picture with a title becomes a figure; the title is its caption.
  useEffect(() => {
    const el = root.current;
    if (!el || !post) return;
    for (const img of el.querySelectorAll("img[title]")) {
      const title = img.getAttribute("title")?.trim();
      if (!title) continue;
      const figure = document.createElement("figure");
      const caption = document.createElement("figcaption");
      caption.textContent = title;
      img.removeAttribute("title");
      const holder = img.parentElement;
      if (holder?.tagName === "P" && holder.childNodes.length === 1) holder.replaceWith(figure);
      else img.replaceWith(figure);
      figure.append(img, caption);
    }
  }, [post]);

  // Pictures kept by Quire are shown from where they are stored.
  useEffect(() => {
    const el = root.current;
    if (!el || !post) return;
    const urls: string[] = [];
    let stale = false;
    for (const img of el.querySelectorAll("img")) {
      const src = img.getAttribute("src");
      if (!isKeptImage(src)) continue;
      app.readImage(src).then((blob) => {
        if (!blob || stale) return;
        const url = URL.createObjectURL(blob);
        urls.push(url);
        img.src = url;
      });
    }
    return () => {
      stale = true;
      for (const url of urls) URL.revokeObjectURL(url);
    };
  }, [post, app]);

  // Fenced code is coloured by its language; each language loads the first time it is needed.
  useEffect(() => {
    const el = root.current;
    if (!el || !post) return;
    const blocks = [...el.querySelectorAll<HTMLElement>("pre > code[class*='language-']")];
    if (blocks.length === 0) return;
    let stale = false;
    import("@quire/editor/highlight").then(async (lib) => {
      for (const code of blocks) {
        const lang = /language-(\S+)/.exec(code.className)?.[1];
        if (!lang || !(await lib.ensureLanguage(lang)) || stale) continue;
        const lines = lib.highlight(lang, code.textContent ?? "");
        if (!lines) continue;
        const parts: Node[] = [];
        lines.forEach((pieces, i) => {
          if (i > 0) parts.push(document.createTextNode("\n"));
          for (const piece of pieces) {
            if (piece.kind === "c-text") {
              parts.push(document.createTextNode(piece.text));
              continue;
            }
            const span = document.createElement("span");
            span.dataset.tok = piece.kind;
            span.textContent = piece.text;
            parts.push(span);
          }
        });
        code.replaceChildren(...parts);
      }
    });
    return () => {
      stale = true;
    };
  }, [post]);

  if (!s.previewOpen || !post) return null;
  return (
    <div
      id="preview"
      class="preview"
      ref={root}
      tabIndex={-1}
      data-site-theme={theme}
      role="dialog"
      aria-label="Preview"
      onKeyDown={(e) => {
        if (e.key === "Escape") app.setPreview(false);
      }}
    >
      <div class="preview-tools">
        <button
          class="button"
          type="button"
          aria-pressed={theme === "dark"}
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
        >
          {theme === "dark" ? "Site light" : "Site dark"}
        </button>
        <button class="button" type="button" onClick={() => app.setPreview(false)}>
          Close preview
        </button>
      </div>
      <article class="post">
        <header class="post-head">
          {post.title && <h1 class="post-title">{post.title}</h1>}
          {post.date && <p class="post-date">{post.date}</p>}
          {post.summary && <p class="post-summary">{post.summary}</p>}
          {post.tags.length > 0 && (
            <ul class="post-tags" aria-label="Tags">
              {post.tags.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          )}
        </header>
        <div class="prose" dangerouslySetInnerHTML={{ __html: post.html }} />
      </article>
    </div>
  );
}
